import React, { useState, useEffect, useCallback } from 'react';
import {
  Mail,
  MailCheck,
  AlertTriangle,
  Users,
  Loader2,
  RefreshCw,
  Inbox,
  CalendarClock,
  ChevronRight,
  UserRound,
} from 'lucide-react';
import { StatusBadge } from '../../../components/UI/StatusBadge';
import Modal from '../../../components/UI/Modal';

/**
 * NotificationCoverage - audit view for the OT / compliance digests.
 *
 * HR mental model: "Were managers and HR actually informed, and when?"
 *   - Digest activity: every scheduled run in the month (sent *and* quiet).
 *     Click "N emailed" → who received that batch (managers / HR).
 *   - Who was notified: recipient + date + cadence across the month.
 *   - Coverage gaps: at-risk workers with no reachable manager chain.
 */

const ROLE_LABELS = {
  trade_leader: 'Trade Leader',
  reporting_officer: 'Reporting Officer',
  lead_trade_leader: 'Lead Trade Leader',
  deputy_hod: 'Deputy HOD',
  hod: 'HOD',
  hr: 'HR',
};

const CADENCE_LABELS = {
  weekly: 'Weekly (Mon)',
  midweek: 'Mid-week (Thu)',
  daily: 'Daily',
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-SG', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (e) {
    return iso;
  }
};

const formatMonthLabel = (yearMonth) => {
  if (!yearMonth) return '';
  try {
    const [y, m] = yearMonth.split('-');
    const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
    return d.toLocaleDateString('en-SG', { month: 'long', year: 'numeric' });
  } catch (e) {
    return yearMonth;
  }
};

const digestRunBadge = (run) => {
  if (run.outcome === 'success' && run.emailsSent > 0) {
    return { status: 'sent', label: `${run.emailsSent} emailed`, drillable: true };
  }
  if (run.outcome === 'skipped_empty') {
    return { status: 'skipped_empty', label: 'No alerts', drillable: false };
  }
  if (run.outcome === 'disabled') {
    return { status: 'disabled', label: 'Disabled', drillable: false };
  }
  if (run.outcome === 'superseded') {
    return { status: 'superseded', label: 'Superseded', drillable: false };
  }
  return { status: run.outcome || 'pending', label: run.outcome || 'Unknown', drillable: false };
};

const formatCoveredWorkers = (workers = [], maxNames = 4) => {
  if (!workers.length) return null;
  const names = workers.map((w) => w.workerName || w.workerStaffId);
  if (names.length <= maxNames) {
    return names.join(', ');
  }
  const shown = names.slice(0, maxNames).join(', ');
  return `${shown} +${names.length - maxNames} more`;
};

/**
 * Outcome cell for Digest Activity. "N emailed" is a button so HR can
 * open the recipient list for that exact batch.
 */
const DigestRunOutcome = ({ run, onOpenRecipients }) => {
  const badge = digestRunBadge(run);

  if (!badge.drillable) {
    return <StatusBadge status={badge.status} label={badge.label} />;
  }

  return (
    <button
      type="button"
      onClick={() => onOpenRecipients(run)}
      className="inline-flex items-center gap-1 rounded-full group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
      aria-label={`View ${run.emailsSent} recipients emailed on ${formatDateTime(run.runAt)}`}
      title="View who was emailed"
    >
      <StatusBadge status={badge.status} label={badge.label} />
      <ChevronRight className="w-3.5 h-3.5 text-green-700 opacity-70 group-hover:opacity-100 transition-opacity" />
    </button>
  );
};

/**
 * Modal: recipients for one digest run ("who are the 8?").
 * These are managers / HR contacts — not the at-risk workers themselves.
 * Worker names appear as secondary context under each recipient.
 */
const DigestRunRecipientsModal = ({ run, isOpen, onClose }) => {
  if (!run) return null;

  const recipients = run.recipients || [];
  const cadenceLabel = CADENCE_LABELS[run.cadence] || run.cadence;
  const expectedCount = run.emailsSent || 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Who was emailed"
      size="lg"
      theme="admin"
      contentClassName="space-y-4"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
          <MailCheck className="w-5 h-5 text-green-600" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900">
            {formatDateTime(run.runAt)}
            <span className="text-gray-400 font-normal"> · </span>
            {cadenceLabel}
          </p>
          <p className="text-sm text-gray-600 mt-0.5">
            {expectedCount} manager{expectedCount === 1 ? '' : 's'} / HR contact
            {expectedCount === 1 ? '' : 's'} received this digest batch.
            At-risk workers named in each email are listed under the recipient.
          </p>
        </div>
      </div>

      {recipients.length > 0 ? (
        <>
          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {recipients.map((r) => {
              const workersLabel = formatCoveredWorkers(r.coveredWorkers);
              return (
                <div
                  key={r.id || `${r.recipientStaffId}-${r.recipientRole}`}
                  className="border border-gray-200 rounded-xl p-4"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {r.recipientName || r.recipientStaffId}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {r.recipientStaffId}
                        {r.recipientRole ? ` · ${ROLE_LABELS[r.recipientRole] || r.recipientRole}` : ''}
                      </p>
                      {r.recipientEmail && (
                        <p className="text-xs text-gray-400 truncate mt-0.5">{r.recipientEmail}</p>
                      )}
                    </div>
                    <div className="flex-shrink-0">
                      <StatusBadge
                        status={r.status}
                        label={r.status === 'pending' ? 'Enqueued' : undefined}
                      />
                    </div>
                  </div>
                  {workersLabel && (
                    <p className="text-xs text-gray-600 mt-2">
                      <span className="font-medium text-gray-700">
                        {r.coveredWorkerCount || r.coveredWorkers?.length || 0} worker
                        {(r.coveredWorkerCount || r.coveredWorkers?.length || 0) === 1 ? '' : 's'} named:
                      </span>{' '}
                      {workersLabel}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Recipient</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Workers named</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {recipients.map((r) => {
                  const workersLabel = formatCoveredWorkers(r.coveredWorkers);
                  const workerCount = r.coveredWorkerCount || r.coveredWorkers?.length || 0;
                  return (
                    <tr key={r.id || `${r.recipientStaffId}-${r.recipientRole}`} className="table-row">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">
                          {r.recipientName || r.recipientStaffId}
                        </p>
                        <p className="text-xs text-gray-500">{r.recipientStaffId}</p>
                        {r.recipientEmail && (
                          <p className="text-xs text-gray-400 truncate max-w-[220px]">{r.recipientEmail}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                        {ROLE_LABELS[r.recipientRole] || r.recipientRole || '—'}
                      </td>
                      <td className="px-4 py-3">
                        {workerCount > 0 ? (
                          <>
                            <p className="text-sm font-medium text-gray-900 tabular-nums">
                              {workerCount}
                            </p>
                            {workersLabel && (
                              <p className="text-xs text-gray-500 mt-0.5 max-w-xs">{workersLabel}</p>
                            )}
                          </>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <StatusBadge
                          status={r.status}
                          label={r.status === 'pending' ? 'Enqueued' : undefined}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="text-center py-10 border border-dashed border-gray-200 rounded-xl">
          <UserRound className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <h4 className="text-sm font-semibold text-gray-900 mb-1">Recipient details unavailable</h4>
          <p className="text-sm text-gray-500 max-w-sm mx-auto">
            This run reported {expectedCount} email{expectedCount === 1 ? '' : 's'} sent, but no
            per-recipient delivery records were found for the batch. Check the month-wide
            &ldquo;Who Was Notified&rdquo; log below.
          </p>
        </div>
      )}
    </Modal>
  );
};

const NotificationCoverage = ({ getNotificationCoverage, selectedMonth }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedRun, setSelectedRun] = useState(null);

  const monthLabel = formatMonthLabel(selectedMonth);

  const load = useCallback(async () => {
    if (!selectedMonth) return;
    setLoading(true);
    try {
      const result = await getNotificationCoverage({ year_month: selectedMonth });
      setData(result);
    } finally {
      setLoading(false);
    }
  }, [getNotificationCoverage, selectedMonth]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = data?.summary || {};
  const gaps = data?.coverageGaps || [];
  const digestRuns = data?.digestRuns || [];
  const recipientNotifications = data?.recipientNotifications || [];

  const digestHasRun =
    Boolean(summary.lastGeneratedAt)
    || recipientNotifications.length > 0
    || gaps.length > 0
    || digestRuns.length > 0;

  const runsSent = summary.digestRunsSent ?? digestRuns.filter(
    (r) => r.outcome === 'success' && r.emailsSent > 0
  ).length;

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="bg-white rounded-2xl shadow-sm border p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Notification Coverage</h3>
            <p className="text-sm text-gray-600 mt-1">
              Were managers and HR informed about at-risk workers in{' '}
              <span className="font-medium text-gray-700">{monthLabel}</span>?
              Digest activity shows every scheduled run — click{' '}
              <span className="font-medium text-gray-700">N emailed</span> to see
              who received that batch. The notification log lists every recipient
              across the month.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={load}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-50 transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Refresh
            </button>
          </div>
        </div>

        {summary.lastGeneratedAt && (
          <p className="text-xs text-gray-400 mt-3">
            Last email batch: {formatDateTime(summary.lastGeneratedAt)}
          </p>
        )}
      </div>

      {/* Summary cards — admin metrics-card pattern (DS §Metrics Cards) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
              <CalendarClock className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{summary.digestRunsTotal || digestRuns.length || 0}</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Digest runs</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <MailCheck className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{runsSent}</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Runs that sent email</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{summary.recipientsNotified || 0}</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Recipients informed</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{summary.coverageGaps || 0}</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Coverage gaps</p>
            </div>
          </div>
        </div>
      </div>

      {/* Digest activity — full month schedule, sent and quiet runs */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2 min-w-0">
          <CalendarClock className="w-5 h-5 text-gray-500 flex-shrink-0" />
          <h3 className="text-lg font-semibold text-gray-900 truncate min-w-0">Digest Activity</h3>
          <span className="text-sm text-gray-500 truncate flex-shrink min-w-0 hidden sm:inline">
            — every scheduled run in {monthLabel}
          </span>
        </div>
        {digestRuns.length > 0 ? (
          <>
            <div className="md:hidden divide-y divide-gray-100">
              {digestRuns.map((run, idx) => (
                <div key={`${run.runAt}-${run.cadence}-${idx}`} className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">{formatDateTime(run.runAt)}</p>
                      <p className="text-xs text-gray-500 mt-0.5 capitalize">
                        {CADENCE_LABELS[run.cadence] || run.cadence}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <DigestRunOutcome run={run} onOpenRecipients={setSelectedRun} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Run date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Cadence</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-600">Outcome</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {digestRuns.map((run, idx) => (
                    <tr key={`${run.runAt}-${run.cadence}-${idx}`} className="table-row">
                      <td className="px-6 py-4 text-sm text-gray-900">{formatDateTime(run.runAt)}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {CADENCE_LABELS[run.cadence] || run.cadence}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <DigestRunOutcome run={run} onOpenRecipients={setSelectedRun} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            {loading ? (
              <>
                <Loader2 className="w-12 h-12 text-gray-300 mx-auto mb-4 animate-spin" />
                <p className="text-sm text-gray-500">Loading digest activity…</p>
              </>
            ) : (
              <>
                <Inbox className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No digest runs logged</h3>
                <p className="text-sm text-gray-500 max-w-md mx-auto">
                  No overtime digest executions were recorded for {monthLabel}.
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Who was notified — recipient + date, no worker lists */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2 min-w-0">
          <Mail className="w-5 h-5 text-gray-500 flex-shrink-0" />
          <h3 className="text-lg font-semibold text-gray-900 truncate min-w-0">Who Was Notified</h3>
          <span className="text-sm text-gray-500 truncate flex-shrink min-w-0 hidden sm:inline">
            — managers and HR emailed about OT risk
          </span>
        </div>
        {recipientNotifications.length > 0 ? (
          <>
            <div className="md:hidden divide-y divide-gray-100">
              {recipientNotifications.map((n) => (
                <div key={n.id} className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{n.recipientName || n.recipientStaffId}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {n.recipientStaffId} · {ROLE_LABELS[n.recipientRole] || n.recipientRole}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <StatusBadge status={n.status} label={n.status === 'pending' ? 'Enqueued' : undefined} />
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-2 text-xs text-gray-500">
                    <span>{CADENCE_LABELS[n.cadence] || n.cadence}</span>
                    <span>{formatDateTime(n.notifiedAt)}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Notified on</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Recipient</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Cadence</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {recipientNotifications.map((n) => (
                    <tr key={n.id} className="table-row">
                      <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                        {formatDateTime(n.notifiedAt)}
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium text-gray-900">{n.recipientName || n.recipientStaffId}</p>
                        <p className="text-xs text-gray-500">{n.recipientStaffId}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {ROLE_LABELS[n.recipientRole] || n.recipientRole}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {CADENCE_LABELS[n.cadence] || n.cadence}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <StatusBadge
                          status={n.status}
                          label={n.status === 'pending' ? 'Enqueued' : undefined}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            {loading ? (
              <>
                <Loader2 className="w-12 h-12 text-gray-300 mx-auto mb-4 animate-spin" />
                <p className="text-sm text-gray-500">Loading notifications…</p>
              </>
            ) : digestHasRun ? (
              <>
                <MailCheck className="w-12 h-12 text-green-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No emails sent</h3>
                <p className="text-sm text-gray-500 max-w-md mx-auto">
                  The digest ran in {monthLabel}, but no manager or HR email was
                  dispatched — everyone stayed below the notification threshold.
                </p>
              </>
            ) : (
              <>
                <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No notifications recorded</h3>
                <p className="text-sm text-gray-500 max-w-md mx-auto">
                  No overtime digest notifications have been logged for {monthLabel} yet.
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Coverage gaps — the headline HR signal */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2 min-w-0">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <h3 className="text-lg font-semibold text-gray-900 truncate min-w-0">Coverage Gaps</h3>
          <span className="text-sm text-gray-500 truncate flex-shrink min-w-0 hidden sm:inline">
            — at-risk workers with no reachable manager
          </span>
        </div>
        {gaps.length > 0 ? (
          <>
            <div className="md:hidden divide-y divide-gray-100">
              {gaps.map((g) => (
                <div key={g.id} className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{g.workerName || g.workerStaffId}</p>
                      <p className="text-xs text-gray-500 truncate">{g.workerStaffId} · {g.department || '—'}</p>
                    </div>
                    {g.severity && <div className="flex-shrink-0"><StatusBadge status={g.severity} /></div>}
                  </div>
                  {g.reason && <p className="text-sm text-gray-600 mt-2">{g.reason}</p>}
                  <p className="text-xs text-gray-400 mt-2">Detected {formatDateTime(g.detectedAt)}</p>
                </div>
              ))}
            </div>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Worker</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Department</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-600">Severity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Why unreachable</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-600">Detected</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {gaps.map((g) => (
                    <tr key={g.id} className="table-row">
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium text-gray-900">{g.workerName || g.workerStaffId}</p>
                        <p className="text-xs text-gray-500">{g.workerStaffId}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{g.department || '—'}</td>
                      <td className="px-6 py-4 text-center">
                        {g.severity ? (
                          <StatusBadge status={g.severity} />
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-md">{g.reason || '—'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 text-right">{formatDateTime(g.detectedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : digestHasRun ? (
          <div className="text-center py-12">
            <MailCheck className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No coverage gaps</h3>
            <p className="text-sm text-gray-500 max-w-md mx-auto">
              Every at-risk worker in {monthLabel} had at least one reachable manager.
            </p>
          </div>
        ) : (
          <div className="text-center py-12">
            <Inbox className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No digest activity yet for {monthLabel}
            </h3>
            <p className="text-sm text-gray-500 max-w-md mx-auto">
              Coverage gaps appear here once an overtime digest dispatches for this month.
            </p>
          </div>
        )}
      </div>

      <DigestRunRecipientsModal
        run={selectedRun}
        isOpen={Boolean(selectedRun)}
        onClose={() => setSelectedRun(null)}
      />
    </div>
  );
};

export default NotificationCoverage;
